
import React, { useCallback, useMemo } from 'react'
import { type NodeProps } from '@xyflow/react'
import { GalleryContainer } from '@/components/canvas/nodes/variants/GalleryContainer'
import type { GenieNodeState } from '@genieartinc/schemas'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'
import { useContainerNavigationStore } from '@/stores/useContainerNavigationStore'

export interface GalleryContainerNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
}

function GalleryContainerNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as GalleryContainerNodeData
  const { state: genieState } = nodeData
  const onStateChange = useNodeStateChange(id)
  const enterContainer = useContainerNavigationStore((s) => s.enterContainer)

  // Read totalItems from node state (updated by BatchGenerator on each item complete)
  const totalItems = (genieState.totalItems as number) ?? 0

  const handleEnterContainer = useCallback(() => {
    enterContainer(id)
  }, [id, enterContainer])

  const containerProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      width: NODE_DIMENSIONS.GalleryContainer?.width,
      height: NODE_DIMENSIONS.GalleryContainer?.height,
      onStateChange,
      state: genieState,
      totalItems,
      mediaType: genieState.mediaType as string | undefined,
      onEnterContainer: handleEnterContainer,
    }),
    [id, genieState, selected, onStateChange, totalItems, handleEnterContainer],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.GalleryContainer

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <GalleryContainer {...containerProps} />
    </>
  )
}

export const GalleryContainerNode = React.memo(
  GalleryContainerNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as GalleryContainerNodeData
    const nextData = nextProps.data as unknown as GalleryContainerNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.seriesName === nextData.state.seriesName &&
      prevData.state.totalItems === nextData.state.totalItems &&
      prevData.state.mediaType === nextData.state.mediaType &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

GalleryContainerNode.displayName = 'GalleryContainerNode'
